import React from 'react';
import { Users, Target, Eye, Code2, Lightbulb, Heart } from 'lucide-react';

const About: React.FC = () => {
  const teamMembers = [
    {
      id: '1',
      name: 'Alex Johnson',
      role: 'CEO & Founder',
      bio: 'Visionary leader with 10+ years in digital transformation and business strategy.',
      image: '/images/team-1.jpg',
      skills: ['Strategic Planning', 'Business Development', 'Team Leadership']
    },
    {
      id: '2',
      name: 'Sarah Chen',
      role: 'CTO & Co-Founder',
      bio: 'Full-stack architect passionate about scalable solutions and emerging technologies.',
      image: '/images/team-2.jpg',
      skills: ['System Architecture', 'Cloud Computing', 'DevOps']
    },
    {
      id: '3',
      name: 'Michael Rodriguez',
      role: 'Lead Frontend Developer',
      bio: 'UI/UX enthusiast creating beautiful and intuitive user experiences.',
      image: '/images/team-3.jpg',
      skills: ['React', 'TypeScript', 'UI/UX Design']
    },
    {
      id: '4',
      name: 'Emily Davis',
      role: 'Backend Developer',
      bio: 'Database optimization expert building robust and secure backend systems.',
      image: '/images/team-4.jpg',
      skills: ['Node.js', 'PostgreSQL', 'API Development']
    },
    {
      id: '5',
      name: 'David Kim',
      role: 'Mobile App Developer',
      bio: 'Cross-platform mobile specialist delivering native-quality experiences.',
      image: '/images/team-5.jpg',
      skills: ['React Native', 'Flutter', 'iOS/Android']
    },
    {
      id: '6',
      name: 'Lisa Thompson',
      role: 'Digital Marketing Specialist',
      bio: 'Growth hacker focused on data-driven marketing strategies and SEO optimization.',
      image: '/images/team-6.jpg',
      skills: ['SEO', 'Content Marketing', 'Analytics']
    }
  ];

  const values = [
    {
      icon: <Code2 className="h-8 w-8" />,
      title: 'Technical Excellence',
      description: 'We maintain the highest standards in code quality, architecture, and development practices.'
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: 'Client-Centric Approach',
      description: 'Your success is our success. We prioritize understanding and exceeding client expectations.'
    },
    {
      icon: <Lightbulb className="h-8 w-8" />,
      title: 'Innovation',
      description: 'We embrace cutting-edge technologies and creative solutions to solve complex challenges.'
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: 'Passion',
      description: 'We love what we do and it shows in every project we deliver and every relationship we build.'
    }
  ];

  const achievements = [
    {
      number: '50+',
      label: 'Projects Completed',
      description: 'Successfully delivered projects across various industries'
    },
    {
      number: '98%',
      label: 'Client Satisfaction',
      description: 'Consistently exceeding client expectations and requirements'
    },
    {
      number: '5+',
      label: 'Years Experience',
      description: 'Deep expertise in modern web and mobile technologies'
    },
    {
      number: '24/7',
      label: 'Support Available',
      description: 'Round-the-clock support for critical business applications'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-12">
          <div className="text-center">
            <div className="mb-4">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30">
                🚀 Our Story
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight">
              About <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Infurasoft</span>
            </h1>
            <p className="text-lg lg:text-xl mb-8 text-blue-100 leading-relaxed max-w-4xl mx-auto">
              We are a passionate team of digital innovators dedicated to transforming businesses through cutting-edge technology solutions and strategic digital consulting.
            </p>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">200+</div>
                <div className="text-blue-200 text-sm">Projects</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">98%</div>
                <div className="text-blue-200 text-sm">Satisfaction</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">5+</div>
                <div className="text-blue-200 text-sm">Years</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-blue-200 text-sm">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-blue-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="mb-8">
                <div className="inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium mb-6">
                  📖 Our Journey
                </div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                  Our <span className="text-blue-600 dark:text-blue-400">Story</span>
                </h2>
              </div>
              <div className="space-y-8 text-gray-600 dark:text-gray-300">
                <div className="relative pl-8 border-l-2 border-blue-200 dark:border-blue-700">
                  <div className="absolute -left-2 top-0 w-4 h-4 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                  <p className="text-lg leading-relaxed">
                    Founded in 2019, Infurasoft began as a vision to bridge the gap between innovative technology and practical business solutions. Our founders, Alex Johnson and Sarah Chen, recognized the need for a digital agency that could deliver both technical excellence and strategic business value.
                  </p>
                </div>
                <div className="relative pl-8 border-l-2 border-blue-200 dark:border-blue-700">
                  <div className="absolute -left-2 top-0 w-4 h-4 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                  <p className="text-lg leading-relaxed">
                    What started as a small team of passionate developers has grown into a full-service digital agency. We've had the privilege of working with startups, SMEs, and enterprise clients across various industries, helping them navigate their digital transformation journeys.
                  </p>
                </div>
                <div className="relative pl-8 border-l-2 border-blue-200 dark:border-blue-700">
                  <div className="absolute -left-2 top-0 w-4 h-4 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                  <p className="text-lg leading-relaxed">
                    Today, we continue to push the boundaries of what's possible in web development, mobile applications, and digital marketing. Our commitment to staying ahead of technology trends ensures that our clients always receive cutting-edge solutions.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 rounded-3xl p-10 text-white shadow-2xl relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm-20 0c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20z'/%3E%3C/g%3E%3C/svg%3E")`,
                  }}></div>
                </div>
                
                <div className="relative z-10">
                  <h3 className="text-3xl font-bold mb-8 flex items-center">
                    <span className="mr-3">🚀</span>
                    Our Journey
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-start group">
                      <div className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mr-4 mt-1 group-hover:scale-110 transition-transform duration-300">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div>
                      <div>
                        <div className="font-bold text-lg mb-1">2019 - Founded</div>
                        <div className="text-blue-100">Started with a vision to transform businesses digitally</div>
                      </div>
                    </div>
                    <div className="flex items-start group">
                      <div className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mr-4 mt-1 group-hover:scale-110 transition-transform duration-300">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div>
                      <div>
                        <div className="font-bold text-lg mb-1">2020 - First Major Client</div>
                        <div className="text-blue-100">Delivered our first enterprise-level solution</div>
                      </div>
                    </div>
                    <div className="flex items-start group">
                      <div className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mr-4 mt-1 group-hover:scale-110 transition-transform duration-300">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div>
                      <div>
                        <div className="font-bold text-lg mb-1">2022 - Team Expansion</div>
                        <div className="text-blue-100">Grew to a team of 15+ specialists</div>
                      </div>
                    </div>
                    <div className="flex items-start group">
                      <div className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mr-4 mt-1 group-hover:scale-110 transition-transform duration-300">
                        <div className="w-3 h-3 bg-white rounded-full"></div>
                      </div>
                      <div>
                        <div className="font-bold text-lg mb-1">2024 - 200+ Projects</div>
                        <div className="text-blue-100">Celebrating 200+ successful project deliveries</div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Elements */}
                <div className="absolute top-6 right-6 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center text-white text-lg animate-pulse">
                  ⭐
                </div>
                <div className="absolute bottom-6 left-6 w-8 h-8 bg-white/10 rounded-full flex items-center justify-center text-white text-sm animate-bounce">
                  🎯
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-indigo-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-indigo-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200 rounded-full text-sm font-medium mb-6">
              🎯 Our Purpose
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Vision & <span className="text-indigo-600 dark:text-indigo-400">Mission</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Our core values and purpose that drive everything we do and shape our commitment to excellence.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="group relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-indigo-200 dark:hover:border-indigo-600 hover:-translate-y-2">
              {/* Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 to-transparent dark:from-indigo-900/20 dark:to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="flex items-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-2xl mr-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                    <Eye className="h-8 w-8" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
                    Our Vision
                  </h2>
                </div>
                <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                  To be the leading digital transformation partner that empowers businesses to thrive in the digital age. We envision a world where technology seamlessly integrates with business strategy to create meaningful impact and sustainable growth.
                </p>
              </div>
              
              {/* Hover Effect Border */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-indigo-500/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
            </div>
            
            <div className="group relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm p-10 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-indigo-200 dark:hover:border-indigo-600 hover:-translate-y-2">
              {/* Background Gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 to-transparent dark:from-indigo-900/20 dark:to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10">
                <div className="flex items-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-2xl mr-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                    <Target className="h-8 w-8" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
                    Our Mission
                  </h2>
                </div>
                <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                  To deliver exceptional digital solutions that drive business growth and enhance user experiences. We are committed to understanding our clients' unique challenges and providing innovative, scalable, and sustainable technology solutions.
                </p>
              </div>
              
              {/* Hover Effect Border */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-indigo-500/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-orange-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-orange-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200 rounded-full text-sm font-medium mb-6">
              💎 Our Values
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              What We <span className="text-orange-600 dark:text-orange-400">Stand For</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              The principles that guide everything we do and shape our company culture, ensuring we deliver exceptional value to our clients.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <div key={index} className="group relative text-center bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-8 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-orange-200 dark:hover:border-orange-600 hover:-translate-y-3 hover:scale-105">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 to-transparent dark:from-orange-900/20 dark:to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 text-white rounded-2xl mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                    {value.description}
                  </p>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-orange-500/20 to-red-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-teal-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-teal-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-teal-100 dark:bg-teal-900/50 text-teal-800 dark:text-teal-200 rounded-full text-sm font-medium mb-6">
              🏆 Our Achievements
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Numbers That <span className="text-teal-600 dark:text-teal-400">Speak</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Numbers that reflect our commitment to excellence and client success, showcasing our track record of delivering exceptional results.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {achievements.map((achievement, index) => (
              <div key={index} className="text-center">
                <div className="group relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-teal-200 dark:hover:border-teal-600 hover:-translate-y-3 hover:scale-105">
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 to-transparent dark:from-teal-900/20 dark:to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  <div className="relative z-10">
                    <div className="text-4xl lg:text-5xl font-bold text-teal-600 dark:text-teal-400 mb-4 group-hover:scale-110 transition-transform duration-500">
                      {achievement.number}
                    </div>
                    <div className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors duration-300">
                      {achievement.label}
                    </div>
                    <div className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                      {achievement.description}
                    </div>
                  </div>
                  
                  {/* Hover Effect Border */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-teal-500/20 to-cyan-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-pink-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-pink-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-pink-100 dark:bg-pink-900/50 text-pink-800 dark:text-pink-200 rounded-full text-sm font-medium mb-6">
              👥 Our Team
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Meet Our <span className="text-pink-600 dark:text-pink-400">Experts</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              The talented individuals who make Infurasoft's success possible. Our diverse team of experts brings together years of experience and passion for innovation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member) => (
              <div key={member.id} className="group relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-pink-200 dark:hover:border-pink-600 hover:-translate-y-3 hover:scale-105">
                {/* Header with Avatar */}
                <div className="h-56 bg-gradient-to-br from-pink-500 via-rose-600 to-purple-600 flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors duration-500"></div>
                  
                  {/* Animated Background Pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                  </div>
                  
                  <div className="w-28 h-28 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-3xl font-bold relative z-10 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white text-sm animate-pulse">
                    ⭐
                  </div>
                  <div className="absolute bottom-4 left-4 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-bounce">
                    💼
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-pink-600 dark:group-hover:text-pink-400 transition-colors duration-300">
                    {member.name}
                  </h3>
                  <div className="text-pink-600 dark:text-pink-400 font-medium mb-4 text-sm">
                    {member.role}
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-6 text-sm leading-relaxed line-clamp-3 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                    {member.bio}
                  </p>
                  
                  {/* Skills */}
                  <div className="flex flex-wrap gap-2">
                    {member.skills.slice(0, 3).map((skill, skillIndex) => (
                      <span key={skillIndex} className="px-3 py-1 bg-gradient-to-r from-pink-100 to-rose-100 dark:from-pink-900/50 dark:to-rose-900/50 text-pink-800 dark:text-pink-200 text-xs rounded-full font-medium border border-pink-200/50 dark:border-pink-700/50 group-hover:scale-105 transition-transform duration-300">
                        {skill}
                      </span>
                    ))}
                    {member.skills.length > 3 && (
                      <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full font-medium">
                        +{member.skills.length - 3}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-pink-500/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30 mb-6">
              🤝 Let's Collaborate
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Work <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">With Us?</span>
          </h2>
          <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed">
            Let's discuss how our expert team can help bring your digital vision to life and transform your business with innovative solutions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <a
              href="/contact"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl bg-white text-blue-600 hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              <span>Get In Touch</span>
            </a>
            <a
              href="/services"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-all duration-300 hover:scale-105"
            >
              <span>View Our Services</span>
            </a>
          </div>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Expert</div>
              <div className="text-blue-200 text-sm">Team</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Proven</div>
              <div className="text-blue-200 text-sm">Results</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-blue-200 text-sm">Support</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;